import moment from "moment";
import {
  ESIC_REASON_CODES,
  TEST_EMPLOYEES,
  runComponents,
  sampleReasonMapping,
  sampleVersions,
} from "pages/PayRoll/Configuration/StatutoryConfiguration/ESI/esiConfigData";
import {
  CODES as ALL_CODES,
  PERIODS,
  codeForLocation,
  codeName,
  coverageFor,
  coverageLabel,
  formatDay,
  money,
  sampleEmployees,
  wageOn,
} from "pages/PayRoll/Statutory/ESI/esiEligibilityData";

// Payroll > Reports > Statutory Reports > ESI (screens R1–R5a), on sample
// data until the ESI APIs exist. Every amount is worked out from the ESI
// Eligibility sample with the same calculation as Configuration's "Test on
// employee", so one employee's ESI is the same everywhere (AC-R13).

export { formatDay, money };
export const num = (value) => Number(value || 0).toLocaleString("en-IN");

export const CODES = ALL_CODES.filter((code) => code.status === "Active");
// "Erode Plant · …001001"
export const codeShort = (code) => (code ? `${codeName(code)} · …${code.code.slice(-6)}` : "Not linked");
export { codeName };

export const PAYROLL_MONTHS = [
  { value: "2026-10", label: "Oct 2026", finalized: false },
  { value: "2026-09", label: "Sep 2026", finalized: true, finalizedAt: "2026-10-01T14:15:00", version: "1.0" },
  { value: "2026-08", label: "Aug 2026", finalized: true, finalizedAt: "2026-09-01T11:30:00", version: "1.0" },
];
export const DEFAULT_MONTH = "2026-09";
// Months with finalised payroll (for History and Download for a period).
export const FINALISED_MONTHS = Array.from({ length: 12 }, (_, index) => moment("2026-09-01").subtract(index, "months").format("YYYY-MM")).reverse();
export const isFinalised = (month) => FINALISED_MONTHS.includes(month);
export const monthLabel = (month) => moment(`${month}-01`).format("MMM YYYY");
// Sample "today" for the checks (mockup: last checked 05 Oct 2026).
export const CHECK_DATE = "2026-10-05";
export const DUE_DAY = 15;
export const dueDate = (month) => moment(`${month}-01`).add(1, "month").date(DUE_DAY);

// ---- Monthly calculation ----

const periodForMonth = (month) => PERIODS.find((period) => !moment(`${month}-01`).isBefore(period.start) && !moment(`${month}-01`).isAfter(period.end));
const versionForMonth = (month) =>
  sampleVersions()
    .filter((version) => version.status !== "Draft" && !moment(version.effective_from).isAfter(`${month}-01`))
    .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0];

// Sample payroll inputs: unpaid days, overtime and whole-month absences.
const UNPAID = { "2026-09": { E003: 2, E007: 1, E008: 30, E112: 2, E130: 3 }, "2026-08": { E003: 1, E120: 2 } };
const OVERTIME = { E002: 1500 };
const ABSENCE = { "2026-09": { E008: "On unpaid leave all month" } };
// The Sep 2026 earnings Configuration's "Test on employee" uses.
const CONFIG_EARNED = Object.fromEntries(TEST_EMPLOYEES.map((employee) => [employee.id, employee]));

export const EMPLOYEES = sampleEmployees();
const byId = Object.fromEntries(EMPLOYEES.map((employee) => [employee.id, employee]));

const overlapDays = (from, until, start, end) => {
  const first = moment.max(moment(from), start);
  const last = moment.min(moment(until), end);
  return last.isBefore(first, "day") ? 0 : last.diff(first, "days") + 1;
};

// One employee's ESI for a month, or null when they aren't employed then.
export const monthRow = (employee, month) => {
  const period = periodForMonth(month);
  if (!period) return null;
  const coverage = coverageFor(employee, period);
  if (!coverage) return null;
  const start = moment(`${month}-01`);
  const end = start.clone().endOf("month");
  if (moment(employee.doj).isAfter(end) || (employee.lwd && moment(employee.lwd).isBefore(start))) return null;
  const code = codeForLocation(employee.location);
  const base = {
    id: employee.id,
    emp_id: employee.emp_id,
    name: employee.name,
    department: employee.department,
    location: employee.location,
    ip_number: employee.ip_number,
    ip_label: employee.ip_number || "Missing",
    code,
    code_label: codeName(code),
    month,
    dim: start.daysInMonth(),
    coverage,
    coverage_label: coverageLabel(coverage),
    absence: ABSENCE[month]?.[employee.id] || "",
  };
  const coveredDays = coverage.status === "COVERED" ? overlapDays(coverage.from, coverage.until, start, end) : 0;
  if (!coveredDays) return { ...base, covered: false, days: 0, wage: 0, ee: 0, er: 0, total: 0, breakdown: {} };

  const days = Math.max(0, coveredDays - (UNPAID[month]?.[employee.id] || 0));
  const config = month === "2026-09" ? CONFIG_EARNED[employee.id] : null;
  let breakdown;
  if (config) {
    breakdown = { BASIC_E: config.BASIC_E, DA_E: config.DA_E, SPECIAL_E: config.SPECIAL_E, OT: config.OT, SHIFT: config.SHIFT };
  } else {
    const earned = Math.round((wageOn(employee, end.format("YYYY-MM-DD")) * days) / base.dim);
    const basic = Math.round(earned * 0.5);
    const da = Math.round(earned * 0.15);
    breakdown = { BASIC_E: basic, DA_E: da, SPECIAL_E: earned - basic - da, OT: days ? OVERTIME[employee.id] || 0 : 0, SHIFT: 0 };
  }
  const daysPaid = config ? config.days_paid : days;
  const values = runComponents(versionForMonth(month), { ...breakdown, INCENTIVE: 0, BONUS: 0, days_paid: daysPaid, covered: true });
  return {
    ...base,
    covered: true,
    days: daysPaid,
    wage: values.ESI_WAGE,
    ee: values.EMP_ESI,
    er: values.EMPR_ESI,
    total: values.EMP_ESI + values.EMPR_ESI,
    breakdown,
    avg: values.AVG_DAILY_WAGE,
    version: versionForMonth(month),
  };
};

const monthCache = {};
export const monthRows = (month) => {
  if (!monthCache[month]) monthCache[month] = EMPLOYEES.map((employee) => monthRow(employee, month)).filter(Boolean);
  return monthCache[month];
};

export const liabilityOf = (rows) => {
  const employee = rows.reduce((sum, row) => sum + row.ee, 0);
  const employer = rows.reduce((sum, row) => sum + row.er, 0);
  return { employee, employer, total: employee + employer, count: rows.filter((row) => row.covered).length };
};
export const codeRows = (month, code) => monthRows(month).filter((row) => row.covered && row.code?.code === code.code);

// History tab: one employee's ESI for each finalised month of the period.
export const historyFor = (employeeId, month) => {
  const period = periodForMonth(month);
  const employee = byId[employeeId];
  if (!period || !employee) return [];
  const months = [];
  for (let cursor = moment(period.start); !cursor.isAfter(period.end); cursor.add(1, "month")) {
    const value = cursor.format("YYYY-MM");
    if (isFinalised(value)) months.push(value);
  }
  return months
    .map((value) => monthRow(employee, value))
    .filter((row) => row && row.covered)
    .reverse();
};

// ---- ESIC file ----

const MAPPING = sampleReasonMapping();
export const reasonCodeFor = (reason) => {
  const code = Object.keys(MAPPING).find((key) => MAPPING[key].includes(reason));
  return code == null ? null : ESIC_REASON_CODES.find((item) => item.code === Number(code));
};
const prevMonth = (month) => moment(`${month}-01`).subtract(1, "month").format("YYYY-MM");

// The row rule (7.3): (a) everyone covered by the code on any day of the
// month; (b) everyone in the code's previous-month file with no covered
// days now — 0 days, wages 0 and a reason code; (c) nobody else.
export const esicRows = (month, code) => {
  const current = codeRows(month, code);
  const rows = current.map((row) => {
    const reason = row.days === 0 ? reasonCodeFor(row.absence) : null;
    return { id: row.id, ip: row.ip_number, name: row.name.toUpperCase(), days: row.days, wages: row.wage, reason, lwd: "", source: row, missingReason: row.days === 0 && !reason };
  });
  codeRows(prevMonth(month), code)
    .filter((before) => !current.some((row) => row.id === before.id))
    .forEach((before) => {
      const employee = byId[before.id];
      const now = monthRow(employee, month);
      const reasonName = employee.lwd ? employee.exit_reason : now && !now.covered ? "Wages above ceiling at new period (system)" : "";
      const reason = reasonName ? reasonCodeFor(reasonName) : null;
      // Out of coverage at a new period: the last covered day is the day
      // before the month starts.
      const lastDay = employee.lwd || (reasonName && !employee.lwd ? moment(`${month}-01`).subtract(1, "day").format("YYYY-MM-DD") : "");
      rows.push({
        id: before.id,
        ip: employee.ip_number,
        name: employee.name.toUpperCase(),
        days: 0,
        wages: 0,
        reason,
        reasonName,
        lwd: reason?.lwd && lastDay ? moment(lastDay).format("DD/MM/YYYY") : "",
        leaver: Boolean(employee.lwd),
        source: before,
        missingReason: !reason,
        missingLwd: Boolean(reason?.lwd && !lastDay),
      });
    });
  return rows;
};

export const esicFileName = (code, month, version) => `ESI_${code.code}_${month}${version ? `_v${version}` : ""}.xls`;
export const ESIC_COLUMNS = ["IP Number", "IP Name", "No. of Days", "Total Monthly Wages", "Reason Code", "Last Working Day"];
// All cells as text (7.3).
export const esicSheetRows = (rows) =>
  rows.map((row) => [row.ip || "", row.name, String(row.days), String(row.wages), row.days === 0 && row.reason ? String(row.reason.code) : "", row.lwd]);
// Small stand-in for the stored checksum.
export const checksumOf = (rows) => {
  let hash = 5381;
  JSON.stringify(esicSheetRows(rows)).split("").forEach((char) => {
    hash = (hash * 33 + char.charCodeAt(0)) % 4294967296;
  });
  return hash.toString(16).padStart(8, "0");
};

export const sampleEsicFiles = () => [
  { id: 4, code: CODES[1].code, month: "2026-09", version: 1, generated_at: "2026-10-12T11:05:00", generated_by: "Priya R", status: "Current", reason: "" },
  { id: 3, code: CODES[0].code, month: "2026-08", version: 2, generated_at: "2026-09-09T10:00:00", generated_by: "Priya R", status: "Current", reason: "IP number added for Divya R" },
  { id: 2, code: CODES[0].code, month: "2026-08", version: 1, generated_at: "2026-09-07T15:40:00", generated_by: "Priya R", status: "Superseded", reason: "" },
  { id: 1, code: CODES[1].code, month: "2026-08", version: 1, generated_at: "2026-09-07T15:45:00", generated_by: "Priya R", status: "Current", reason: "" },
];

// ---- Reconciliation ----

export const RECON_STATUS_COLORS = { "Not recorded": "#6B7280", "Partly paid": "#DC2626", Paid: "#12B76A", Overpaid: "#F0954A" };
export const reconStatus = (liability, paid) => (!paid ? "Not recorded" : paid < liability ? "Partly paid" : paid > liability ? "Overpaid" : "Paid");
export const reconLabel = (status, liability, paid) =>
  status === "Partly paid" ? `Difference ${money(liability - paid)}` : status === "Overpaid" ? `${money(paid - liability)} extra` : status;

// amount_delta: the sample challan amount is the liability plus this.
export const sampleChallans = () => [
  { id: 1, code: CODES[1].code, month: "2026-09", kind: "Original", amount_delta: 0, reference: "ESIC-CH-99213", paid_on: "2026-10-12", receipt: "esic_challan_sep26_chennai.pdf", remarks: "", recorded_by: "Priya R", recorded_at: "2026-10-12T16:20:00", history: [] },
  { id: 2, code: CODES[0].code, month: "2026-08", kind: "Original", amount_delta: 0, reference: "ESIC-CH-97412", paid_on: "2026-09-13", receipt: "esic_challan_aug26_erode.pdf", remarks: "", recorded_by: "Priya R", recorded_at: "2026-09-13T12:00:00", history: [] },
  { id: 3, code: CODES[1].code, month: "2026-08", kind: "Original", amount_delta: -300, reference: "ESIC-CH-97413", paid_on: "2026-09-13", receipt: "esic_challan_aug26_chennai.pdf", remarks: "", recorded_by: "Priya R", recorded_at: "2026-09-13T12:05:00", history: [] },
];
export const withAmounts = (challans) =>
  challans.map((challan) => (challan.amount != null ? challan : { ...challan, amount: liabilityOf(codeRows(challan.month, CODES.find((code) => code.code === challan.code))).total + challan.amount_delta }));

// ---- Validation (7.5) ----

export const SEVERITY_COLORS = { Blocking: "#DC2626", Warning: "#F0954A", Info: "#2563EB" };
export const FIX_ROUTES = {
  Eligibility: { path: "/admin/payroll/statutory/esi", label: "Open Eligibility" },
  "Employer Codes": { path: "/admin/payroll/configuration/statutory-configuration?item=esi&tab=employer-codes", label: "Open Employer Codes" },
  "Reason Codes": { path: "/admin/payroll/configuration/statutory-configuration?item=esi&tab=reason-codes", label: "Open Reason Codes" },
  "Exit record": { path: "/admin/hrzone/employees", label: "Open Employee" },
};

const issue = (fields) => ({ steps: [], ...fields, code_label: fields.code ? codeName(fields.code) : fields.code_label || "—" });

export const buildIssues = (month) => {
  const label = monthLabel(month);
  const rows = monthRows(month);
  const issues = [];
  const check = moment(CHECK_DATE);
  const covered = rows.filter((row) => row.covered);

  covered.forEach((row) => {
    const fileEffect = row.code ? `${codeName(row.code)} ESIC file for ${label} cannot be generated` : "Not in any ESIC file";
    if (!row.ip_number) {
      const due = moment(row.coverage.from).add(10, "days");
      const joined = moment(row.coverage.from).format("DD MMM YYYY");
      const overdue = check.isAfter(due, "day");
      issues.push(
        issue({
          id: `IP-${row.id}`,
          rule: overdue ? "IP_MISSING" : "IP_DUE",
          emp_id: row.emp_id,
          name: row.name,
          department: row.department,
          code: row.code,
          severity: overdue ? "Blocking" : "Warning",
          issue: overdue ? `IP number missing (joined ${joined})` : `IP number due by ${due.format("DD MMM YYYY")} (joined ${moment(row.coverage.from).format("DD MMM")})`,
          problem: "IP number missing",
          detail: { label: "Joined", value: `${joined} — ${overdue ? "more than 10 days ago" : `due by ${due.format("DD MMM")}`}` },
          effect: overdue ? fileEffect : "No effect yet — becomes Blocking after the due date",
          fix_in: "Eligibility",
          steps: [
            ["Register on the ESIC portal", "Get the IP number"],
            ["Add it in Cauver", "Statutory › ESI › Eligibility › Edit"],
            ["Re-run validation", "Then generate the file"],
          ],
        }),
      );
    }
    if (row.coverage.reason === "CONTINUING") {
      issues.push(
        issue({
          id: `CONT-${row.id}`,
          rule: "CONTINUATION",
          emp_id: row.emp_id,
          name: row.name,
          department: row.department,
          code: row.code,
          severity: "Info",
          issue: `Above ceiling — covered until ${moment(row.coverage.until).format("DD MMM")} (continuation)`,
          problem: "Wage above the ceiling but covered",
          detail: { label: "Rule", value: "Stay covered until the period ends" },
          effect: `Re-tested at the next period start (${formatDay(moment(row.coverage.until).add(1, "day"))})`,
          fix_in: "—",
        }),
      );
    }
    if (row.coverage.reason === "OVERRIDE") {
      issues.push(
        issue({
          id: `OVR-${row.id}`,
          rule: "OVERRIDE",
          emp_id: row.emp_id,
          name: row.name,
          department: row.department,
          code: row.code,
          severity: "Info",
          issue: "Coverage override active",
          problem: "Coverage set by an override",
          detail: { label: "Reason", value: row.coverage.rule },
          effect: "Coverage follows the override, not the wage test",
          fix_in: "Eligibility",
          steps: [["Review the override", "Statutory › ESI › Eligibility › Edit"]],
        }),
      );
    }
  });

  // Locations with covered employees and no active code.
  const unlinked = {};
  covered.filter((row) => !row.code).forEach((row) => {
    unlinked[row.location] = (unlinked[row.location] || 0) + 1;
  });
  Object.entries(unlinked).forEach(([location, count]) =>
    issues.push(
      issue({
        id: `LOC-${location}`,
        rule: "LOCATION_UNLINKED",
        emp_id: `${count} employees`,
        name: location,
        code: null,
        code_label: "Not linked",
        severity: "Blocking",
        issue: `Work location “${location}” is not linked to an ESI code`,
        problem: "Location has no ESI code",
        detail: { label: "Employees", value: `${count} covered employees at ${location}` },
        effect: "Their ESI can't be filed in any ESIC file",
        fix_in: "Employer Codes",
        steps: [
          ["Link the location", "Configuration › Statutory › ESI › Employer Codes"],
          ["Re-run validation", "Then generate the files"],
        ],
      }),
    ),
  );

  // 0-day rows and leavers, per code file.
  CODES.forEach((code) => {
    esicRows(month, code)
      .filter((row) => row.days === 0)
      .forEach((row) => {
        const base = { emp_id: row.source.emp_id, name: row.source.name, department: row.source.department, code };
        if (row.missingReason) {
          issues.push(
            issue({
              ...base,
              id: `REASON-${row.id}`,
              rule: "NO_REASON",
              severity: row.reasonName ? "Blocking" : "Warning",
              issue: row.reasonName ? `0 days — “${row.reasonName}” is not mapped to an ESIC reason code` : "Was in last month's file but is missing this month (no exit)",
              problem: row.reasonName ? "No reason code for a 0-day row" : "Missing from this month",
              detail: { label: "Last month", value: `${monthLabel(prevMonth(month))} ESIC file` },
              effect: row.reasonName ? `${codeName(code)} ESIC file for ${label} cannot be generated` : "Check employment or record the reason",
              fix_in: row.reasonName ? "Reason Codes" : "Exit record",
              steps: [["Map the reason", "Configuration › Statutory › ESI › Reason Codes"], ["Re-run validation", "Then generate the file"]],
            }),
          );
        } else if (row.missingLwd) {
          issues.push(
            issue({
              ...base,
              id: `LWD-${row.id}`,
              rule: "NO_LWD",
              severity: "Blocking",
              issue: `Reason ${row.reason.code} needs a last working day`,
              problem: "Last working day missing",
              detail: { label: "Reason", value: `${row.reason.code} · ${row.reason.label}` },
              effect: `${codeName(code)} ESIC file for ${label} cannot be generated`,
              fix_in: "Exit record",
              steps: [["Record the exit date", "People › Employee › Exit"]],
            }),
          );
        } else if (row.leaver && row.reason?.removes) {
          issues.push(
            issue({
              ...base,
              id: `LEFT-${row.id}`,
              rule: "LEAVER",
              severity: "Info",
              issue: `Left ${moment(byId[row.id].lwd).format("DD MMM")} — reason ${row.reason.code}; removed from portal record after upload`,
              problem: "Leaver reported with 0 days",
              detail: { label: "Reason", value: `${row.reason.code} · ${row.reason.label} · last working day ${row.lwd}` },
              effect: "The IP is removed from the employer's portal record after upload",
              fix_in: "—",
            }),
          );
        }
      });
  });

  const order = { Blocking: 0, Warning: 1, Info: 2 };
  return issues.sort((a, b) => order[a.severity] - order[b.severity]);
};
